import IconRefresh from '@cx/ui/Icons/IconRefresh';
import { Badge } from '@interstate/components/Badge';
import { IconButton } from '@interstate/components/IconButton';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { AltDealStructureProviders } from '../../baseDealActivity/baseDealContainer/AltDealStructureProviders';
import { AiVehicleRecommendationProvider } from '../../baseDealActivity/baseDealContainer/AiVehicleRecommendationSlideout/AiVehicleRecommendationProvider';
import type { AiInsights } from '../../baseDealActivity/baseDealContainer/hooks/useAiInsights';
import { InsightGroup, InsightLink, InsightLoading, InsightSeparator, InsightsList } from './ScenarioAiInsights.style';
import { ScenarioAltDealStructureLink } from './ScenarioAltDealStructureLink';
import { ScenarioVehicleRecommendationsLink } from './ScenarioVehicleRecommendationsLink';

export const LENDER_APPROVAL_LABEL = 'Lender Approval Likelihood';
export const LENDER_APPROVAL_LOADING_LABEL = 'Loading Lender Approval Likelihood...';
export const RECOMMENDATIONS_LOADING_LABEL = 'Loading AI Recommendations...';

const INSIGHT_BADGE_SX = { fontSize: '12px', lineHeight: '16px' };

export const ScenarioAiInsights = ({ scopeId, insights }: { scopeId: string; insights: AiInsights }) => {
    const theme = useInterstateTheme();
    const isAffordabilityDisabled = !insights.hasLenderProbabilities;

    return (
        <InsightsList data-testid="scenario-ai-insights">
            {insights.showAffordabilityLoader && (
                <InsightLoading data-testid="ai-vehicle-affordability-loader" aria-hidden>
                    {LENDER_APPROVAL_LOADING_LABEL}
                </InsightLoading>
            )}
            {insights.showAffordabilityCard && (
                <InsightGroup
                    ref={insights.hasLenderProbabilities ? insights.affordabilityCardInViewRef : undefined}
                    data-testid="ai-card-vehicle-affordability-container"
                >
                    <InsightLink
                        type="button"
                        theme={theme}
                        onClick={insights.openLenderLikelihood}
                        disabled={isAffordabilityDisabled}
                        data-testid="ai-card-vehicle-affordability-label"
                    >
                        {LENDER_APPROVAL_LABEL}
                    </InsightLink>
                    {insights.lenderApprovalLevel && !isAffordabilityDisabled && (
                        <Badge
                            data-testid="ai-card-vehicle-affordability-badge"
                            variant={insights.affordabilityStatus}
                            sx={INSIGHT_BADGE_SX}
                        >
                            {insights.lenderApprovalLevel}
                        </Badge>
                    )}
                    {insights.shouldShowAIRefreshButton && (
                        <IconButton
                            data-testid="ai-card-vehicle-affordability-refresh-button"
                            onClick={insights.refreshInsights}
                            size="sm"
                            aria-label="Refresh"
                            buttonStyle="tertiary"
                        >
                            <IconRefresh />
                        </IconButton>
                    )}
                </InsightGroup>
            )}
            {insights.showRecommendationsLoader && (
                <>
                    <InsightSeparator theme={theme} />
                    <InsightLoading data-testid="ai-vehicle-recommendations-loader" aria-hidden>
                        {RECOMMENDATIONS_LOADING_LABEL}
                    </InsightLoading>
                </>
            )}
            {insights.showRecommendationsCard && (
                <>
                    <InsightSeparator theme={theme} />
                    <AiVehicleRecommendationProvider>
                        <ScenarioVehicleRecommendationsLink scopeId={scopeId} isDisabled={insights.isRecommendationsCTADisabled} />
                    </AiVehicleRecommendationProvider>
                </>
            )}
            {insights.showAltDealStructuresCard && (
                <>
                    <InsightSeparator theme={theme} />
                    <AltDealStructureProviders>
                        <ScenarioAltDealStructureLink />
                    </AltDealStructureProviders>
                </>
            )}
        </InsightsList>
    );
};
